import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import i18n from "@/i18n";
import { useTranslation } from "react-i18next";

export interface AppointmentBelonging {
  id: string;
  appointment_id: string;
  name: string;
  returned: boolean;
  returned_at: string | null;
  returned_by: string | null;
  created_at: string;
  created_by: string;
}

const APPOINTMENT_BELONGINGS_SELECT =
  "id, appointment_id, name, returned, returned_at, returned_by, created_at, created_by";
const APPOINTMENT_BELONGINGS_STALE_TIME = 30_000;

const sortBelongings = (rows: AppointmentBelonging[]) =>
  [...rows].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

const getAuthenticatedUserId = async () => {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;

  const userId = sessionData.session?.user?.id;
  if (!userId) throw new Error(i18n.t("calendar:phase8.appointmentBelongings.errors.notAuthenticated"));

  return userId;
};

export function useAppointmentBelongings(appointmentId?: string, enabled = true) {
  return useQuery({
    queryKey: ["appointment-belongings", appointmentId],
    queryFn: async (): Promise<AppointmentBelonging[]> => {
      if (!appointmentId) return [];
      // Table added by migration; cast to any while generated types are not refreshed.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("appointment_belongings")
        .select(APPOINTMENT_BELONGINGS_SELECT)
        .eq("appointment_id", appointmentId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data ?? []) as AppointmentBelonging[];
    },
    enabled: Boolean(appointmentId) && enabled,
    staleTime: APPOINTMENT_BELONGINGS_STALE_TIME,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useAddAppointmentBelonging() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("calendar");

  return useMutation({
    mutationFn: async ({ appointmentId, name }: { appointmentId: string; name: string }) => {
      const trimmedName = name.trim();
      if (!trimmedName) throw new Error(t("phase8.appointmentBelongings.errors.nameRequired"));

      const userId = await getAuthenticatedUserId();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("appointment_belongings")
        .insert({
          appointment_id: appointmentId,
          name: trimmedName,
          returned: false,
          created_by: userId,
        })
        .select(APPOINTMENT_BELONGINGS_SELECT)
        .single();

      if (error) throw error;
      return data as AppointmentBelonging;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<AppointmentBelonging[]>(
        ["appointment-belongings", variables.appointmentId],
        (current) => sortBelongings([...(current ?? []), data]),
      );
      toast({
        title: t("phase8.appointmentBelongings.addedTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.appointmentBelongings.addErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useSetAppointmentBelongingReturned() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("calendar");

  return useMutation({
    mutationFn: async ({
      appointmentId,
      itemId,
      returned,
    }: {
      appointmentId: string;
      itemId: string;
      returned: boolean;
    }) => {
      const userId = await getAuthenticatedUserId();

      const payload = returned
        ? {
            returned: true,
            returned_at: new Date().toISOString(),
            returned_by: userId,
          }
        : {
            returned: false,
            returned_at: null,
            returned_by: null,
          };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("appointment_belongings")
        .update(payload)
        .eq("id", itemId)
        .eq("appointment_id", appointmentId)
        .select(APPOINTMENT_BELONGINGS_SELECT)
        .single();

      if (error) throw error;
      return data as AppointmentBelonging;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<AppointmentBelonging[]>(
        ["appointment-belongings", variables.appointmentId],
        (current) =>
          (current ?? []).map((item) => (item.id === data.id ? data : item)),
      );
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.appointmentBelongings.returnUpdateErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteAppointmentBelonging() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("calendar");

  return useMutation({
    mutationFn: async ({ appointmentId, itemId }: { appointmentId: string; itemId: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("appointment_belongings")
        .delete()
        .eq("id", itemId)
        .eq("appointment_id", appointmentId);

      if (error) throw error;
      return { itemId };
    },
    onSuccess: (_data, variables) => {
      queryClient.setQueryData<AppointmentBelonging[]>(
        ["appointment-belongings", variables.appointmentId],
        (current) => (current ?? []).filter((item) => item.id !== variables.itemId),
      );
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.appointmentBelongings.deleteErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
